<template>
  <main class="results-page" :class="{ 'results-page--no-gift': isNoGiftAvailable }">
    <div class="results-content">
      <section class="result-summary" aria-live="polite">
        <p class="result-eyebrow">{{ $t(`matchingResultsPage.outcomes.${outcomeKey}.eyebrow`) }}</p>
        <h1 class="result-score">{{ correctCount }}/{{ totalCount }}</h1>
        <p class="result-title" :class="`result-title--${outcomeKey}`">
          <span>{{ $t(`matchingResultsPage.outcomes.${outcomeKey}.title`) }}</span>
          <span>{{ $t(`matchingResultsPage.outcomes.${outcomeKey}.message`) }}</span>
        </p>
      </section>

      <section v-if="!isNoGiftAvailable" class="prizes-section">
        <h2 class="section-title">
          {{ $t('matchingResultsPage.prizesTitle') }}
        </h2>
        <div class="prize-list">
          <div v-for="(gift, index) in displayGifts" :key="gift.id || index" class="prize-pill">
            {{ gift.name }}
            <span v-if="gift.code" class="prize-code"> — {{ gift.code }}</span>
          </div>
        </div>
      </section>

      <section class="coffees-section">
        <h2 class="section-title">{{ $t('matchingResultsPage.coffeesTitle') }}</h2>
        <img class="coffees-image" src="@/assets/specialitiesMatching.png"
          :alt="$t('matchingResultsPage.coffeesImageAlt')">
      </section>

      <p class="terms">{{ termsMessage }}</p>

      <button type="button" class="return-button" @click="$emit('return-home')">
        {{ $t('matchingResultsPage.returnButton') }}
      </button>
    </div>
  </main>
</template>

<script>
export default {
  name: 'MatchingResultsPage',
  props: {
    matchingResult: {
      type: Object,
      default: () => ({})
    }
  },
  emits: ['return-home'],
  computed: {
    isNoGiftAvailable() {
      const response = this.matchingResult?.apiResponse;
      return response &&
        typeof response === 'object' &&
        response.error === 'GIFTS_SOLD_OUT';
    },
    termsMessage() {
      if (this.isNoGiftAvailable) {
        return this.$t('matchingResultsPage.noGiftTerms', {
          correctCount: this.correctCount,
          totalCount: this.totalCount
        });
      }

      return this.$t('matchingResultsPage.terms');
    },
    correctCount() {
      const response = this.matchingResult?.apiResponse;
      const apiCorrectCount = response && typeof response === 'object'
        ? response.correctCount ??
        response.correct ??
        response.score ??
        response.results?.correctCount ??
        response.data?.correctCount
        : null;
      const score = Number(apiCorrectCount ?? this.matchingResult?.correctCount);

      if (!Number.isFinite(score)) return 0;
      return Math.min(this.totalCount, Math.max(0, Math.round(score)));
    },
    totalCount() {
      const total = Number(this.matchingResult?.totalCount);
      return Number.isFinite(total) && total > 0 ? Math.round(total) : 3;
    },
    outcomeKey() {
      return String(Math.min(3, Math.max(0, this.correctCount)));
    },
    apiGifts() {
      return this.findGifts(this.matchingResult?.apiResponse)
        .flatMap((gift, index) => this.normalizeGifts(gift, index))
        .filter((gift) => gift.name);
    },
    displayGifts() {
      if (this.isNoGiftAvailable) return [];
      if (this.apiGifts.length) return this.apiGifts;

      return [
        { id: 'capsules', name: this.$t('matchingResultsPage.defaultPrizes.capsules') },
        { id: 'cold-brew', name: this.$t('matchingResultsPage.defaultPrizes.coldBrew') }
      ];
    }
  },
  methods: {
    findGifts(response) {
      if (!response) return [];
      if (Array.isArray(response)) return response;
      if (typeof response === 'string') return [{ name: response }];

      const giftValue =
        response.gifts ??
        response.gift ??
        response.prizes ??
        response.prize ??
        response.rewards ??
        response.reward ??
        response.data?.gifts ??
        response.data?.gift ??
        response.data?.prizes ??
        response.data?.prize;

      if (giftValue == null) return [];
      return Array.isArray(giftValue) ? giftValue : [giftValue];
    },
    normalizeGift(gift, index) {
      if (typeof gift === 'string' || typeof gift === 'number') {
        return {
          id: index,
          name: this.localizeGiftName(String(gift)),
          code: ''
        };
      }

      const name = gift.name ?? gift.title ?? gift.label ?? gift.giftName ?? gift.prizeName ?? '';

      return {
        id: gift.id ?? gift.index ?? index,
        name: this.localizeGiftName(name, gift),
        code: gift.code ?? gift.promoCode ?? gift.promo_code ?? ''
      };
    },
    normalizeGifts(gift, index) {
      const giftIdentity = this.getGiftIdentity(gift);
      const includesCapsules = /capsul|καψουλ/.test(giftIdentity);
      const includesColdBrew = /cold[\s_-]*brew/.test(giftIdentity);

      if (includesCapsules && includesColdBrew) {
        return [
          {
            id: `${index}-capsules`,
            name: this.$t('matchingResultsPage.defaultPrizes.capsules'),
            code: ''
          },
          {
            id: `${index}-cold-brew`,
            name: this.$t('matchingResultsPage.defaultPrizes.coldBrew'),
            code: ''
          }
        ];
      }

      return [this.normalizeGift(gift, index)];
    },
    getGiftIdentity(gift) {
      const giftData = gift && typeof gift === 'object' ? gift : {};
      const name = typeof gift === 'string' || typeof gift === 'number'
        ? String(gift)
        : giftData.name ?? giftData.title ?? giftData.label ?? giftData.giftName ?? giftData.prizeName ?? '';

      return [
        giftData.id,
        giftData.type,
        giftData.slug,
        giftData.key,
        name
      ]
        .filter((value) => value != null)
        .join(' ')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase();
    },
    localizeGiftName(name, gift = {}) {
      const giftIdentity = this.getGiftIdentity({ ...gift, name });

      if (/cold[\s_-]*brew/.test(giftIdentity)) {
        return this.$t('matchingResultsPage.defaultPrizes.coldBrew');
      }

      if (/capsul|καψουλ/.test(giftIdentity)) {
        return this.$t('matchingResultsPage.defaultPrizes.capsules');
      }

      return name;
    }
  }
};
</script>

<style scoped>
.results-page {
  padding: min(4.8148vw, 104px) 0 min(5.5556vw, 120px);
  color: #fff;
}

.results-page--no-gift {
  padding-top: min(12.0370vw, 260px);
}

.results-content {
  width: 100%;
  margin-inline: auto;
  text-align: center;
}

.result-summary p,
.result-summary h1,
.section-title,
.terms {
  margin: 0;
}

.result-eyebrow {
  font-size: min(5.0926vw, 110px);
  font-weight: 500;
  line-height: min(5.7870vw, 125px);
}

.result-score {
  margin-top: 0 !important;
  font-size: min(14.9537vw, 323px);
  font-weight: 500;
  line-height: min(17.5463vw, 379px);
}

.result-title {
  display: flex;
  flex-direction: column;
  margin: 0 auto !important;
  font-size: min(4.0741vw, 88px);
  font-weight: 500;
  line-height: min(4.5833vw, 99px);
}

.result-title--0 {
  padding-inline: min(8.1019vw, 175px);
}

.result-title--1 {
  padding-inline: min(13.3333vw, 288px);
}

.result-title--2 {
  padding-inline: min(16.8519vw, 364px);
}

.result-title--3 {
  padding-inline: min(15.6667vw, 355px);
}

.prizes-section {
  margin-top: min(6.8519vw, 148px);
}

.section-title {
  display: inline-block;
  border-bottom: min(0.1852vw, 4px) solid currentColor;
  font-size: min(5.3704vw, 116px);
  font-weight: 500;
  line-height: min(6.5278vw, 141px);
  text-transform: uppercase;
}

.prize-list {
  display: flex;
  justify-content: center;
  flex-wrap: nowrap;
  gap: min(2.3148vw, 50px);
  margin-top: min(2.2222vw, 48px);
}

.prize-pill {
  min-width: min(33.3333vw, 720px);
  padding: min(1.6667vw, 36px) min(3.7500vw, 81px);
  border: min(0.2778vw, 6px) solid #0090d4;
  border-radius: min(4.3056vw, 93px);
  font-size: min(4.6296vw, 100px);
  font-weight: 500;
  line-height: min(5.2778vw, 114px);
}

.prize-code {
  white-space: nowrap;
}

.coffees-section {
  margin-top: min(6.8519vw, 148px);
}

.results-page--no-gift .coffees-section {
  margin-top: min(7.8704vw, 170px);
}

.coffees-image {
  display: block;
  width: min(62.1759vw, 1343px);
  height: min(32.4074vw, 700px);
  margin: min(2.9630vw, 64px) auto 0;
  object-fit: contain;
}

.terms {
  width: min(89.5370vw, 1934px);
  margin: min(1.6667vw, 36px) auto 0;
  font-family: 'CorporateFont', sans-serif;
  font-size: min(3.5185vw, 76px);
  font-weight: 300;
  line-height: min(4.2130vw, 91px);
  white-space: pre-line;
}

.return-button {
  display: block;
  width: min(80.9259vw, 1748px);
  margin: min(6.1111vw, 132px) auto 0;
  padding: min(1.5741vw, 34px) min(25.5093vw, 551px);
  border: 0;
  border-radius: min(4.5370vw, 98px);
  background: #0090d4 0% 0% no-repeat padding-box;
  color: #fff;
  font-family: 'DrukFont', sans-serif;
  font-size: min(4.8148vw, 104px);
  font-weight: 500;
  line-height: min(5.4167vw, 117px);
  text-transform: uppercase;
  cursor: pointer;
}

.results-page--no-gift .return-button {
  margin-top: min(20.1389vw, 435px);
}
</style>
